/**
 * ReleasesPanel — admin tool to draft and broadcast a release-note email.
 *
 * Flow: type a short "what shipped" hint → hit Draft (LLM writes it) → review →
 * Publish + Email All. Also lists the last few releases so admin can edit or
 * delete a bad one. Kept as its own component so Admin.jsx doesn't balloon.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { errMsg } from "@/lib/errors";
import { Send, Sparkles, Loader2, Trash2, Mail } from "lucide-react";

export default function ReleasesPanel() {
  const [hint, setHint] = useState("");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [releases, setReleases] = useState([]);

  const loadReleases = () => api.get("/releases?limit=8")
    .then((r) => setReleases(r.data.releases || []))
    .catch(() => setReleases([]));

  useEffect(() => { loadReleases(); }, []);

  const doDraft = async () => {
    if (!hint.trim()) { toast.error("Give me a one-liner about what shipped"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/releases/draft", { hint, send: false });
      setDraft(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const publish = async (send) => {
    if (!hint.trim()) { toast.error("Add a hint first"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/admin/releases", { hint, send });
      toast.success(send
        ? `Release published + ${data.sent}/${data.recipients} emails sent`
        : "Release published (no emails)");
      setHint(""); setDraft(null);
      loadReleases();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };

  const delRelease = async (id) => {
    if (!window.confirm("Delete this release note?")) return;
    try { await api.delete(`/admin/releases/${id}`); loadReleases(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <section
      data-testid="admin-releases-panel"
      className="border border-[#2A2A2A] bg-[#121212] p-5 mb-10"
    >
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-5 w-5 text-[#FF4500]" />
        <div className="font-display text-lg">Release notes broadcast</div>
      </div>
      <div className="font-mono text-[11px] text-gray-500 mb-4">
        Drop a one-liner about what you just shipped. We ask the AI to draft a warm email,
        publish it to <a href="/releases" className="text-[#FF4500]">/releases</a>, and (optionally) email every user who hasn't opted out.
      </div>

      <textarea
        data-testid="releases-hint"
        value={hint}
        onChange={(e) => setHint(e.target.value)}
        placeholder="e.g. Community Chat is live, Kunaki CDs coming next week, small perf fixes"
        rows={3}
        maxLength={2000}
        className="w-full bg-black border border-[#2A2A2A] px-3 py-2 font-mono text-xs focus:outline-none focus:border-[#FF4500] mb-3"
      />

      <div className="flex flex-wrap gap-2 mb-4">
        <Button
          data-testid="releases-draft-btn"
          onClick={doDraft}
          disabled={busy || !hint.trim()}
          variant="outline"
          className="rounded-none border-[#2A2A2A] bg-transparent hover:border-[#FF4500] hover:text-[#FF4500] font-mono text-[10px] uppercase tracking-widest h-9"
        >
          {busy ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1.5" />}
          AI Draft preview
        </Button>
        <Button
          data-testid="releases-publish-silent"
          onClick={() => publish(false)}
          disabled={sending || !hint.trim()}
          variant="outline"
          className="rounded-none border-[#2A2A2A] bg-transparent font-mono text-[10px] uppercase tracking-widest h-9"
        >
          Publish (no email)
        </Button>
        <Button
          data-testid="releases-publish-email"
          onClick={() => publish(true)}
          disabled={sending || !hint.trim()}
          className="rounded-none bg-[#FF4500] hover:bg-[#E53E00] text-black font-mono text-[10px] uppercase tracking-widest h-9"
        >
          {sending ? <Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> : <Send className="h-3 w-3 mr-1.5" />}
          Publish + Email All
        </Button>
      </div>

      {draft && (
        <div data-testid="releases-draft" className="border border-[#FF4500]/40 bg-[#FF4500]/[0.05] p-4 mb-4">
          <div className="font-mono text-[9px] uppercase tracking-widest text-[#FF4500] mb-1">Preview</div>
          <div className="font-display text-lg mb-1">{draft.title}</div>
          <div className="font-mono text-[11px] text-gray-400 mb-3">{draft.subtitle}</div>
          <div
            className="prose prose-invert prose-sm max-w-none text-sm text-gray-200"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(draft.body_html) }}
          />
        </div>
      )}

      {/* Past releases */}
      <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2 mt-4">
        Recent releases · {releases.length}
      </div>
      {releases.length === 0 && (
        <div className="font-mono text-[11px] italic text-gray-600">No releases yet.</div>
      )}
      <ul className="divide-y divide-[#1a1a1a]">
        {releases.map((r) => (
          <li key={r.id} data-testid={`release-row-${r.id}`} className="py-2 flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-display text-sm truncate">{r.title}</div>
              <div className="font-mono text-[10px] text-gray-500 truncate">
                {new Date(r.created_at).toLocaleString()} · {r.sent_count || 0}/{r.recipients_total || 0} emailed
              </div>
            </div>
            <button
              data-testid={`release-delete-${r.id}`}
              onClick={() => delRelease(r.id)}
              title="Delete"
              className="h-8 w-8 grid place-items-center border border-[#2A2A2A] hover:border-red-500 hover:text-red-500"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
